import React from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/style/colors";
import AnimatedTextButton from "../generic/AnimatedTextButton";
import InstallAsset from "../../assets/imgs/landingPage/installs.webp";
import LandingAsset from "../../assets/imgs/landingPage/genericLanding.webp";
import ReferralAsset from "../../assets/imgs/landingPage/referral.webp";
import MessageAsset from "../../assets/imgs/landingPage/message.webp";
import DataAsset from "../../assets/imgs/landingPage/data.webp";
import { useNavigate } from "react-router-dom";


const SolveMarketingChallenges = () => {

  const navigate = useNavigate();

  return (
    <FullPageContainer>
      <Container id="marketingChallenges">
        <Line>
          <TextWrapper>
            <Title>
              Solve your marketing challenges with clarity and control.
            </Title>
            <Subtitle>
              Grovs delivers precise tracking and actionable insights, helping
              you optimize every aspect of your campaigns. From identifying
              high-performing ads to guiding users to their desired content,
              Grovs ensures your marketing strategy is both effective and
              efficient.
            </Subtitle>
          </TextWrapper>
        </Line>

        <Line>
          <InfoTopic>
            <InfoTopicTitle>
              Do you know exactly which campaigns drive installs and
              reactivations?
            </InfoTopicTitle>
            <InfoTopicDescription>
              If you’re guessing which ads work, you’re wasting budget. Grovs
              tracks every click, install, and reactivation across platforms and
              devices—giving you clear, actionable insights to boost ROI and
              scale faster.
            </InfoTopicDescription>
            <AnimatedTextButton
              light="light"
              text={"Explore Marketing solutions"}
              style={{
                color: lightThemeColors.lightColor,
                borderBottom: `1px solid ${lightThemeColors.lightColor}`,
                fontWeight: "600",
              }}
              onClick={() => navigate("/marketing")}
            />
          </InfoTopic>
          <CardWrapper

          >
            <ImageContainer>
              <img src={InstallAsset} alt="install" />
            </ImageContainer>
          </CardWrapper>
        </Line>

        <Line>
          <InfoTopic

          >
            <InfoTopicTitle>
              Are your users landing where they need to—or getting lost?
            </InfoTopicTitle>
            <InfoTopicDescription>
              Generic landing pages frustrate users and kill conversions. Grovs’
              smart links send users straight to the exact content you
              want—whether that’s a product page, promo, or feature—on iOS,
              Android, or after install. No dead ends. No drop-offs.
            </InfoTopicDescription>
            <AnimatedTextButton
              text={"Explore Product solutions"}
              light="light"
              style={{
                color: lightThemeColors.lightColor,
                borderBottom: `1px solid ${lightThemeColors.lightColor}`,
                fontWeight: "600",
              }}
              onClick={() => navigate("/product")}
            />
          </InfoTopic>
          <CardWrapper

          >
            <ImageContainer>
              <img src={LandingAsset} alt="landing" />
            </ImageContainer>
          </CardWrapper>
        </Line>

        <Line>
          <InfoTopic

          >
            <InfoTopicTitle>
              Are you making the most of every referral and user invitation?
            </InfoTopicTitle>
            <InfoTopicDescription>
              Your users can be your best marketers—if you can track them. Grovs
              makes referral tracking simple, letting you reward advocates and
              turn word-of-mouth into low-cost, high-value installs.
            </InfoTopicDescription>
            <AnimatedTextButton
              text={"Explore Data & Analytics solutions"}
              light="light"
              style={{
                color: lightThemeColors.lightColor,
                borderBottom: `1px solid ${lightThemeColors.lightColor}`,
                fontWeight: "600",
              }}
              onClick={() => navigate("/data-analytics")}
            />
          </InfoTopic>
          <CardWrapper

          >
            <ImageContainer>
              <img src={ReferralAsset} alt="referral" />
            </ImageContainer>
          </CardWrapper>
        </Line>

        <Line>
          <InfoTopic

          >
            <InfoTopicTitle>
              Can you engage users across all your apps without overwhelming
              them?
            </InfoTopicTitle>
            <InfoTopicDescription>
              Sending relevant messages across apps shouldn’t feel like herding
              cats. With Grovs, you can automate targeted alerts and promotions
              from one place, using in-app, push, and inbox messaging—ensuring
              users stay engaged without being spammed.
            </InfoTopicDescription>
            <AnimatedTextButton
              text={"Explore Alerts platform"}
              light="light"
              style={{
                color: lightThemeColors.lightColor,
                borderBottom: `1px solid ${lightThemeColors.lightColor}`,
                fontWeight: "600",
              }}
              onClick={() => navigate("/messaging")}
            />
          </InfoTopic>
          <CardWrapper

          >
            <ImageContainer>
              <img src={MessageAsset} alt="install" />
            </ImageContainer>
          </CardWrapper>
        </Line>

        <Line>
          <InfoTopic

          >
            <InfoTopicTitle>
              Do you have the data to make fast, confident marketing decisions?
            </InfoTopicTitle>
            <InfoTopicDescription>
              You don’t need more dashboards—you need insights that drive
              action. Grovs gives you a clean, unified view of campaign
              performance, user behavior, and ROI so you can stop guessing and
              start growing with confidence.
            </InfoTopicDescription>
            <AnimatedTextButton
              text={"Explore Measure platform"}
              light="light"
              style={{
                color: lightThemeColors.lightColor,
                borderBottom: `1px solid ${lightThemeColors.lightColor}`,
                fontWeight: "600",
              }}
              onClick={() => navigate("/measure")}
            />
          </InfoTopic>
          <CardWrapper

          >
            <ImageContainer>
              <img src={DataAsset} alt="install" />
            </ImageContainer>
          </CardWrapper>
        </Line>
      </Container>
    </FullPageContainer>
  );
};

export default SolveMarketingChallenges;

const FullPageContainer = styled.div`
  display: flex;
  width: 100%;
  background: ${lightThemeColors.darkColor};
  position: relative;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1512px;
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: start;
padding:150px 100px 80px;
  gap: 100px;
  @media (max-width: 990px) {
    padding: 20px;
    gap: 20px;
  }
`;

const Line = styled.section`
  display: flex;
  align-items: center;
  justify-content:center;
  gap: 150px;
  width: 100%;
  overflow: hidden;
  flex-direction: ${(props) => (props.reverse ? "row-reverse" : "initial")};

  @media (max-width: 990px) {
    gap: 10px;
    flex-direction: column-reverse;
  }
`;
const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 30px;
  max-width: 850px;
  margin: auto;
`;

const InfoTopic = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 30px;
  max-width: 500px;
`;
const InfoTopicTitle = styled.h3`
  color: ${lightThemeColors.lightColor};
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
`;
const InfoTopicDescription = styled.p`
  color: ${lightThemeColors.lightColorFaded7};
  font-weight: 400;
  font-size: 18px;
  line-height: 24px;
`;

const Title = styled.h1`
  color: ${lightThemeColors.lightColor};
  font-size: 36px;
  font-weight: 700;
  line-height: 46px;
  text-align: center;
  @media (max-width: 990px) {
    font-size: 30px;
    line-height: 36px;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  color: ${lightThemeColors.lightColorFaded7};
  font-size: 18px;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
  @media (max-width: 990px) {
    line-height: 24px;
  }
`;
const CardWrapper = styled.div`
  display: flex;
  height: auto;
  align-items:center;
  justify-content:center;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center; /* Horizontally centers the image */
  align-items: center; /* Vertically centers the image */
  width: 100%;
  height: 100%; /* Set height if needed for vertical centering */
  overflow: hidden;
  max-width: 550px; /* Optional: limit the width of the image container */

  img{

  width:100%;
  }
 
`;
